import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import trainingApi from '../../services/trainingApi';

const TrainingJoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const invitationToken = searchParams.get('invitation');

  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const validate = async () => {
      if (!code && !invitationToken) {
        setError('No invitation code or token provided.');
        setLoading(false);
        return;
      }
      try {
        if (code) {
          const res = await trainingApi.validateTrainingCode(code);
          const body = res.data || res;
          const data = body.data || body;
          setInfo(data);
        } else {
          setInfo({ cohortName: 'Training Cohort', organisationName: 'Training Organisation' });
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Invalid or expired invitation.');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [code, invitationToken]);

  const handleJoin = async () => {
    try {
      setJoining(true);
      setError(null);
      const invCode = code || invitationToken;
      const res = await trainingApi.joinCohort(invCode);
      const body = res.data || res;
      const data = body.data || body;
      setSuccess(data.cohortName || 'the cohort');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to join cohort.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Paper sx={{ p: 4, maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <SchoolIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

        {loading ? (
          <Box><CircularProgress /><Typography sx={{ mt: 2 }}>Validating invitation...</Typography></Box>
        ) : error ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => navigate('/training')}>Go to Dashboard</Button>
          </Box>
        ) : success ? (
          <Box>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>Successfully Joined!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You have been enrolled in {success}. You can now access your assignments and start training sessions.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/training')}>Go to Dashboard</Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>Join Training Cohort</Typography>
            {info?.organisationName && (
              <Typography variant="body1" color="text.secondary" gutterBottom>{info.organisationName}</Typography>
            )}
            {info?.cohortName && (
              <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>{info.cohortName}</Typography>
            )}
            {info?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{info.description}</Typography>
            )}
            {info?.welcomeMessage && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">{info.welcomeMessage}</Typography>
              </Paper>
            )}
            {info?.usesRemaining != null && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {info.usesRemaining} place{info.usesRemaining !== 1 ? 's' : ''} remaining
              </Typography>
            )}
            <Button variant="contained" size="large" fullWidth onClick={handleJoin} disabled={joining} sx={{ mt: 2 }}>
              {joining ? 'Joining...' : 'Join Cohort'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TrainingJoinPage;
